import { FunctionComponent, useEffect, useState } from 'react'
import { EventDay, ReservationChild, ReservationParent } from '@/types/EventDay'
import BookmarkAdded from '@mui/icons-material/BookmarkAdded'
import CalendarMonth from '@mui/icons-material/CalendarMonth'
import ChildCareOutlined from '@mui/icons-material/ChildCareOutlined'
import Comment from '@mui/icons-material/Comment'
import Face from '@mui/icons-material/Face'
import RemoveRedEye from '@mui/icons-material/RemoveRedEye'
import Groups from '@mui/icons-material/Groups'
import styles from './eventDay.module.scss'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import PresenceDialog from './Dialog/presenceDialog/presenceDialog.tsx'
import { Daycare } from '@/types/Daycare'
import EditEventDayDialog from './Dialog/editEventDay/editEventDayDialog.tsx'
import ConfirmDeleteEventDayDialog from './Dialog/confirmDeleteEventDay/confirmDeleteEventDayDialog.tsx'
import ParentReservationDialog from './Dialog/parentReservation/parentReservationDialog.tsx'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper'
import { Section } from '@/types/Section'
import { useScreenSize } from '@/modules/desktopMobile/screenSizeContext'
import { format, isAfter, parse } from 'date-fns'
import { ContentCopy, Delete, EventBusy, Forum, LockClock, StickyNote2 } from '@mui/icons-material'
import DuplicateEventDayDialog from '../Dialog/duplicateEventDay.tsx'
import { useLazyQuery } from '@apollo/client'
import { GET_RESERVATION_EVENT } from '@/modules/API/daycare/eventDayHandler.ts'
import ConfirmDeleteReservationDialog from './Dialog/confirmDeleteReservation/confirmDeleteReservationDialog.tsx'

interface EventDayProps {
    event: EventDay
    isParent?: boolean
    canUpdate: boolean
    daycare?: Daycare
    refetchAllEvents: () => Promise<void>
}

const EventDayRow: FunctionComponent<EventDayProps> = ({
    event,
    isParent = false,
    canUpdate,
    daycare,
    refetchAllEvents
}: EventDayProps) => {
    const { t } = useTranslation()

    const isMobile: boolean = useScreenSize()

    const [isPresenceDialogOpen, setPresenceDialogOpen] = useState<boolean>(false)
    const [isEditDialogOpen, setEditDialogOpen] = useState<boolean>(false)
    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false)

    const [isDeleteReservationDialogOpen, setDeleteReservationDialogOpen] = useState<boolean>(false)
    const [isParentReservationDialogOpen, setParentReservationDialogOpen] = useState<boolean>(false)

    const [eventBooked, setEventBooked] = useState<boolean>(event.booked || false)

    const [isDuplicateEventDialogOpen, setDuplicateEventDialogOpen] = useState<boolean>(false)

    const [daycareComment, setDaycareComment] = useState<string>('')
    const [parentComment, setParentComment] = useState<string>('')

    const [adultsName, setAdultsName] = useState<string>('')
    const [childsName, setChildsName] = useState<string>('')

    const [reservationId, setReservationId] = useState<number>(0)

    const [getReservation, { data: dataReservation }] = useLazyQuery(GET_RESERVATION_EVENT, {
        variables: {
            event_id: event.id
        }
    })

    useEffect(() => {
        if (dataReservation) {
            setReservationId(dataReservation.getReservationEvent.id)
            if (dataReservation.getReservationEvent.reservationParent.length > 0) {
                setAdultsName(
                    dataReservation.getReservationEvent.reservationParent
                        .map((parent: ReservationParent): string => parent.name)
                        .join(', ')
                )
            }
            if (dataReservation.getReservationEvent.reservationChild.length > 0) {
                setChildsName(
                    dataReservation.getReservationEvent.reservationChild
                        .map((reservation: ReservationChild) => reservation.childName)
                        .join(', ')
                )
            }
            setDaycareComment(dataReservation.getReservationEvent.daycareComment)
            setParentComment(dataReservation.getReservationEvent.parentComment)
        }
    }, [dataReservation])

    useEffect(() => {
        if (isParent && event.booked) {
            setAdultsName('')
            setChildsName('')
            getReservation()
        }
    }, [event])

    async function refetchAllEventsAndPresence(): Promise<void> {
        await refetchAllEvents()
        if (isParent && event.booked) {
            setAdultsName('')
            setChildsName('')
            await getReservation()
        }
    }

    return (
        <div className={`${styles.EventDayPlacement} ${isParent && styles.EventDayParent}`}>
            <div className={styles.TitleContainer}>
                <CalendarMonth className={`${isParent ? styles.IconParent : styles.IconDaycare}`} />
                <div className={styles.TextContainer}>
                    <p className={styles.Title}>{event.title}</p>
                    <p className={styles.Date}>
                        {format(parse(event.date, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                            locale: getLocalFromLanguage()
                        }) +
                            ' ' +
                            t('from').toLowerCase() +
                            ' ' +
                            format(parse(event.startHour, 'HH:mm:ss', new Date()), 'HH:mm') +
                            ' ' +
                            t('to').toLowerCase() +
                            ' ' +
                            format(parse(event.endHour, 'HH:mm:ss', new Date()), 'HH:mm')}
                    </p>
                </div>
            </div>
            {(event.message ||
                (!isParent && (event.sections.length > 0 || event.reservation)) ||
                childsName !== '' ||
                adultsName !== '') && <div className={styles.BreakLine}></div>}
            {event.message && (
                <div className={styles.Container}>
                    <Comment className={`${isParent ? styles.IconParent : styles.IconDaycare}`} />
                    <p className={styles.Text}>{event.message}</p>
                </div>
            )}
            {childsName !== '' && (
                <div className={styles.Container}>
                    <ChildCareOutlined className={`${isParent ? styles.IconParent : styles.IconDaycare}`} />
                    <p className={styles.Text}>{childsName}</p>
                </div>
            )}
            {adultsName !== '' && (
                <div className={styles.Container}>
                    <Face className={`${isParent ? styles.IconParent : styles.IconDaycare}`} />
                    <p className={styles.Text}>{adultsName}</p>
                </div>
            )}
            {!isParent && (
                <div className={styles.Container}>
                    <Groups className={`${isParent ? styles.IconParent : styles.IconDaycare}`} />
                    <p className={styles.Text}>
                        {event.sections.length > 0 && daycare && daycare.sections
                            ? event.sections
                                  .map(
                                      (section: number): string | undefined =>
                                          daycare.sections?.find((s: Section): boolean => s.id === section)?.name
                                  )
                                  .join(', ')
                            : t('visibleAll')}
                    </p>
                </div>
            )}
            {event.parentOnly && (
                <div className={styles.Container}>
                    <Face className={`${isParent ? styles.IconParent : styles.IconDaycare}`} />
                    <p className={styles.Text}> {t('parentOnly')}</p>
                </div>
            )}
            {event.releaseDate && isAfter(parse(event.releaseDate, 'dd-MM-yyyy HH:mm:ss', new Date()), new Date()) && (
                <div className={styles.Container}>
                    <LockClock className={styles.IconDaycare} />
                    <p className={styles.Text}>
                        {format(
                            parse(event.releaseDate, 'dd-MM-yyyy HH:mm:ss', new Date()),
                            'EEEE dd MMMM yyyy HH:mm',
                            {
                                locale: getLocalFromLanguage()
                            }
                        )}
                    </p>
                </div>
            )}
            {event.reservation && (
                <>
                    {!isParent && (
                        <>
                            <div className={styles.BreakLine}></div>
                            <div className={styles.Container}>
                                <Face className={styles.IconDaycare} />
                                <p className={styles.Text}>
                                    <span className={styles.Bold}>
                                        {event.attendantBooked}/{event.attendant}
                                    </span>{' '}
                                    {t('adults').toLowerCase()}
                                </p>
                            </div>
                            {!event.parentOnly && (
                                <div className={styles.Container}>
                                    <ChildCareOutlined className={styles.IconDaycare} />
                                    <p className={styles.Text}>
                                        <span className={styles.Bold}>
                                            {event.placesBooked}/{event.places}
                                        </span>{' '}
                                        {t('childs').toLowerCase()}
                                    </p>
                                </div>
                            )}
                            {event.expiryDate && (
                                <div className={styles.Container}>
                                    <EventBusy className={styles.IconDaycare} />
                                    <p className={styles.Text}>
                                        {format(
                                            parse(event.expiryDate, 'dd-MM-yyyy HH:mm:ss', new Date()),
                                            'EEEE dd MMMM yyyy HH:mm',
                                            {
                                                locale: getLocalFromLanguage()
                                            }
                                        )}
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                    {(daycareComment || parentComment) && <div className={styles.BreakLine}></div>}
                    {daycareComment && (
                        <div className={styles.Container}>
                            <StickyNote2 className={`${isParent ? styles.IconParent : styles.IconDaycare}`} />
                            <p className={styles.Text}>{daycareComment}</p>
                        </div>
                    )}
                    {parentComment && (
                        <div className={styles.Container}>
                            <Forum className={`${isParent ? styles.IconParent : styles.IconDaycare}`} />
                            <p className={styles.Text}>{parentComment}</p>
                        </div>
                    )}
                    <div className={styles.ButtonContainerPresence}>
                        {isParent ? (
                            <>
                                {isAfter(
                                    parse(`${event.date} ${event.endHour}`, 'dd-MM-yyyy HH:mm:ss', new Date()),
                                    new Date()
                                ) ? (
                                    <>
                                        <ButtonTextIcon
                                            type={'custom'}
                                            onClick={() => setParentReservationDialogOpen(true)}
                                            customText={eventBooked ? t('editBook') : t('book')}
                                            customIcon={<BookmarkAdded />}
                                            parent
                                            className={styles.Button}
                                        />
                                        {eventBooked && (
                                            <ButtonTextIcon
                                                type={'custom'}
                                                customText={t('deleteReservation')}
                                                customIcon={<Delete />}
                                                onClick={() => setDeleteReservationDialogOpen(true)}
                                                parent
                                                className={styles.Button}
                                            />
                                        )}
                                    </>
                                ) : eventBooked ? (
                                    <p className={styles.Text}>
                                        {t('youHadBookedEvent')}, {t('reservationNotAvailable').toLowerCase()}
                                    </p>
                                ) : (
                                    <p className={styles.Text}>{t('reservationNotAvailable')}</p>
                                )}
                            </>
                        ) : (
                            <ButtonTextIcon
                                type={'custom'}
                                onClick={() => setPresenceDialogOpen(true)}
                                customText={isMobile ? t('presence') : t('showPresence')}
                                customIcon={<RemoveRedEye />}
                                className={styles.FullWidth}
                            />
                        )}
                    </div>
                </>
            )}
            {canUpdate && !isParent && (
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('duplicate')}
                        customIcon={<ContentCopy />}
                        onClick={() => setDuplicateEventDialogOpen(true)}
                        className={styles.Button}
                    />
                    {isAfter(
                        parse(`${event.date} ${event.endHour}`, 'dd-MM-yyyy HH:mm:ss', new Date()),
                        new Date()
                    ) && (
                        <ButtonTextIcon
                            type={'edit'}
                            onClick={() => setEditDialogOpen(true)}
                            className={styles.Button}
                        />
                    )}
                    <ButtonTextIcon
                        type={'delete'}
                        onClick={() => setDeleteDialogOpen(true)}
                        className={styles.Button}
                    />
                </div>
            )}
            {isPresenceDialogOpen && (
                <PresenceDialog
                    open={isPresenceDialogOpen}
                    handleClose={() => setPresenceDialogOpen(false)}
                    event={event}
                    daycare={daycare}
                    refetchAllEvents={refetchAllEvents}
                />
            )}
            {isEditDialogOpen && (
                <EditEventDayDialog
                    event={event}
                    daycareId={event.daycareId}
                    daycareType={daycare?.type}
                    open={isEditDialogOpen}
                    handleClose={() => setEditDialogOpen(false)}
                    refetchAllEvents={refetchAllEvents}
                />
            )}
            {isDeleteDialogOpen && (
                <ConfirmDeleteEventDayDialog
                    event={event}
                    daycareId={event.daycareId}
                    open={isDeleteDialogOpen}
                    handleClose={() => setDeleteDialogOpen(false)}
                    refetchAllEvents={refetchAllEvents}
                />
            )}
            {isParentReservationDialogOpen && (
                <ParentReservationDialog
                    open={isParentReservationDialogOpen}
                    event={event}
                    daycareId={event.daycareId}
                    handleClose={() => setParentReservationDialogOpen(false)}
                    setEventBooked={setEventBooked}
                    isParent={isParent}
                    refetchAllEvents={refetchAllEventsAndPresence}
                />
            )}
            {isDuplicateEventDialogOpen && (
                <DuplicateEventDayDialog
                    event={event}
                    daycare={daycare}
                    open={isDuplicateEventDialogOpen}
                    handleClose={() => setDuplicateEventDialogOpen(false)}
                    refetchAllEvents={refetchAllEvents}
                />
            )}
            {isDeleteReservationDialogOpen && reservationId !== 0 && (
                <ConfirmDeleteReservationDialog
                    open={isDeleteReservationDialogOpen}
                    handleClose={() => setDeleteReservationDialogOpen(false)}
                    daycareId={event.daycareId}
                    reservationId={reservationId}
                    refetchAllEvents={refetchAllEventsAndPresence}
                />
            )}
        </div>
    )
}

export default EventDayRow
